<script lang="ts" setup>
import { clamp } from '@/types/util'

const props = defineProps<{
  orientation: 'horizontal' | 'vertical'
  draggable?: boolean
  container: HTMLDivElement | undefined
  dragIsLegal?: (dragEvent: DragEvent) => boolean
}>()

const emit = defineEmits<{
  drop: [DragEvent]
  drag: [number]
}>()

const dragHovered = ref(false)

function onDragOver(dragEvent: DragEvent) {
  const dataTransfer = dragEvent.dataTransfer!
  if (!dataTransfer.types.some((x) => x.startsWith('application/x-page-element-'))) {
    return
  }

  if (props.dragIsLegal && !props.dragIsLegal(dragEvent)) {
    return
  }

  dragEvent.preventDefault()

  if (dataTransfer.types.includes('application/x-page-element-type')) {
    dataTransfer.dropEffect = 'copy'
  } else if (dataTransfer.types.includes('application/x-page-element-id')) {
    dataTransfer.dropEffect = 'move'
  }

  dragHovered.value = true
}

function onDragLeave() {
  dragHovered.value = false
}

function drop(dragEvent: DragEvent) {
  dragHovered.value = false
  emit('drop', dragEvent)
}

const dragging = ref(false)

function onPointerDown(event: PointerEvent) {
  if (!props.draggable) {
    return
  }

  dragging.value = true
  event.stopPropagation()

  document.documentElement.style.cursor = 'ew-resize'
  document.body.style.pointerEvents = 'none'
  document.body.style.userSelect = 'none'

  function onPointerMove(moveEvent: PointerEvent) {
    const rect = props.container!.getBoundingClientRect()
    const t = clamp((moveEvent.clientX - rect.left) / rect.width, 0, 1)
    const x = Math.round(6 * t)

    emit('drag', x)
  }
  window.addEventListener('pointermove', onPointerMove)

  window.addEventListener(
    'pointerup',
    () => {
      dragging.value = false

      document.documentElement.style.cursor = ''
      document.body.style.pointerEvents = ''
      document.body.style.userSelect = ''

      window.removeEventListener('pointermove', onPointerMove)
    },
    { once: true },
  )
}
</script>

<template>
  <div
    class="group"
    :class="{
      'py-1': orientation === 'horizontal',
      'px-1': orientation === 'vertical',
      'cursor-ew-resize': draggable,
    }"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="drop"
    @pointerdown="onPointerDown"
  >
    <div
      class="rounded-full"
      :class="{
        'bg-primary-500': dragHovered || dragging,
        'w-full h-1': orientation === 'horizontal',
        'w-1 h-full': orientation === 'vertical',
        'group-hover:bg-primary-500': draggable,
      }"
    />
  </div>
</template>
